import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Input, Modal, Form, Select, Tag, Space,
  Card, message, Tooltip, Popconfirm, Tabs, Badge, InputNumber, Divider,
} from 'antd';
import {
  SafetyCertificateOutlined, PlusOutlined, SearchOutlined,
  HomeOutlined, EditOutlined, DeleteOutlined, LinkOutlined,
  VideoCameraOutlined, DatabaseOutlined, WifiOutlined, KeyOutlined, CopyOutlined,
} from '@ant-design/icons';
import axiosInstance from '../utils/axios';
import { hssColors, commonStyles } from '../config/theme';
import { isAdmin } from '../utils/auth';
import Footer from '../components/Footer';
import CreatableSelect from '../components/CreatableSelect';

const MARCAS_DVR_DEFAULT = ['Hikvision', 'Dahua', 'Hanwha', 'Axis', 'Uniview'];
const MARCAS_CAM_DEFAULT = ['Hikvision', 'Dahua', 'Reolink', 'Imou', 'Axis', 'Uniview'];
const MARCAS_ROUTER_DEFAULT = ['Mikrotik', 'TP-Link', 'Teltonika', 'Sierra Wireless', 'Pepwave'];

const { Option } = Select;
const ESTADO_COLOR = { desinstalado: 'warning', instalado: 'success', baja: 'error' };
const ESTADO_LABEL = { desinstalado: 'Desinstalado', instalado: 'Instalado', baja: 'Baja' };
const TIPOS_CAMARA = ['interior', 'exterior', 'domo', 'bala', 'ptz', 'fisheye'];

const HSSVision = () => {
  const navigate = useNavigate();
  const [dvrs, setDvrs] = useState([]);
  const [camaras, setCamaras] = useState([]);
  const [routers, setRouters] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marcasDvr, setMarcasDvr] = useState(MARCAS_DVR_DEFAULT);
  const [modelosDvr, setModelosDvr] = useState([]);
  const [marcasCam, setMarcasCam] = useState(MARCAS_CAM_DEFAULT);
  const [modelosCam, setModelosCam] = useState([]);
  const [marcasRouter, setMarcasRouter] = useState(MARCAS_ROUTER_DEFAULT);
  const [modelosRouter, setModelosRouter] = useState([]);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('dvr');

  // Modals state
  const [modal, setModal] = useState({ tipo: null, modo: null, item: null });
  const [form] = Form.useForm();
  const [credModal, setCredModal] = useState({ open: false, tipo: null, item: null });

  const cargar = async () => {
    setLoading(true);
    try {
      const [dvrRes, camRes, routRes, vehRes] = await Promise.all([
        axiosInstance.get('/vision/dvr'),
        axiosInstance.get('/vision/camaras'),
        axiosInstance.get('/vision/routers'),
        axiosInstance.get('/vehiculos'),
      ]);
      setDvrs(dvrRes.data);
      setCamaras(camRes.data);
      setRouters(routRes.data);
      setVehiculos(vehRes.data);
      // Derivar marcas/modelos únicos de datos existentes
      setMarcasDvr(prev => [...new Set([...prev, ...dvrRes.data.map(d => d.marca).filter(Boolean)])]);
      setModelosDvr(prev => [...new Set([...prev, ...dvrRes.data.map(d => d.modelo).filter(Boolean)])]);
      setMarcasCam(prev => [...new Set([...prev, ...camRes.data.map(c => c.marca).filter(Boolean)])]);
      setModelosCam(prev => [...new Set([...prev, ...camRes.data.map(c => c.modelo).filter(Boolean)])]);
      setMarcasRouter(prev => [...new Set([...prev, ...routRes.data.map(r => r.marca).filter(Boolean)])]);
      setModelosRouter(prev => [...new Set([...prev, ...routRes.data.map(r => r.modelo).filter(Boolean)])]);
    } catch { message.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const abrirModal = (tipo, modo, item = null) => {
    setModal({ tipo, modo, item });
    if (modo === 'editar' && item) {
      form.setFieldsValue(item);
    } else {
      form.resetFields();
      form.setFieldsValue({
        estado: 'desinstalado',
        usuario_web: 'admin',
        password_web: 'Consultores2016',
        usuario_local: 'admin',
        password_local: 'Consultores2016',
        wifi_nombre: 'HSS Vision',
      });
    }
  };
  const cerrarModal = () => { setModal({ tipo: null, modo: null, item: null }); form.resetFields(); };

  const onSubmit = async (values) => {
    const { tipo, modo, item } = modal;
    const url = { dvr: '/vision/dvr', camara: '/vision/camaras', router: '/vision/routers' }[tipo];
    try {
      if (modo === 'nuevo') await axiosInstance.post(url, values);
      else if (modo === 'editar') await axiosInstance.put(`${url}/${item.id}`, values);
      else if (modo === 'asignar') await axiosInstance.put(`${url}/${item.id}`, { vehiculo_chapa: values.vehiculo_chapa || null });
      message.success('Operación exitosa');
      cerrarModal();
      cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error'); }
  };

  const onEliminar = async (tipo, id) => {
    const url = { dvr: '/vision/dvr', camara: '/vision/camaras', router: '/vision/routers' }[tipo];
    try {
      await axiosInstance.delete(`${url}/${id}`);
      message.success('Eliminado'); cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error al eliminar'); }
  };

  const filtrar = (lista) => {
    if (!search) return lista;
    const s = search.toLowerCase();
    return lista.filter(x =>
      x.numero_serie?.toLowerCase().includes(s) ||
      x.marca?.toLowerCase().includes(s) ||
      x.modelo?.toLowerCase().includes(s) ||
      x.vehiculo?.toLowerCase().includes(s) ||
      x.vehiculo_nombre_gps?.toLowerCase().includes(s) ||
      x.numero_linea?.includes(s) ||
      x.tipo?.includes(s)
    );
  };

  const copiar = (valor, label) => {
    if (!valor) return;
    navigator.clipboard.writeText(valor).then(() => message.success(`${label} copiado`));
  };

  const accionesCol = (tipo) => ({
    title: '', key: 'acc', width: tipo === 'camara' ? 110 : 140,
    render: (_, r) => (
      <Space>
        {(tipo === 'dvr' || tipo === 'router') && (
          <Tooltip title="Ver credenciales">
            <Button type="text" icon={<KeyOutlined />} style={{ color: '#d4a017' }}
              onClick={() => setCredModal({ open: true, tipo, item: r })} />
          </Tooltip>
        )}
        <Tooltip title="Asignar vehículo">
          <Button type="text" icon={<LinkOutlined />} style={{ color: hssColors.info }}
            onClick={() => abrirModal(tipo, 'asignar', r)} />
        </Tooltip>
        <Tooltip title="Editar">
          <Button type="text" icon={<EditOutlined />} style={{ color: hssColors.primary }}
            onClick={() => abrirModal(tipo, 'editar', r)} />
        </Tooltip>
        {isAdmin() && (
          <Popconfirm title="¿Eliminar?" onConfirm={() => onEliminar(tipo, r.id)} okText="Sí" cancelText="No">
            <Button type="text" icon={<DeleteOutlined />} style={{ color: hssColors.error }} />
          </Popconfirm>
        )}
      </Space>
    ),
  });

  const vehiculoCol = {
    title: 'Vehículo', dataIndex: 'vehiculo_chapa', key: 'veh',
    render: (chapa) => chapa
      ? <span style={{ color: hssColors.primary, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'monospace' }}
          onClick={() => navigate(`/vehiculos/${chapa}`)}>{chapa}</span>
      : <span style={{ color: hssColors.gray400 }}>Sin asignar</span>,
  };

  const estadoCol = {
    title: 'Estado', dataIndex: 'estado', key: 'estado',
    render: e => <Tag color={ESTADO_COLOR[e] || 'default'}>{ESTADO_LABEL[e] || e}</Tag>,
  };

  const columnsDVR = [
    { title: 'N° Serie', dataIndex: 'numero_serie', key: 'serie', render: v => <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{v}</span> },
    { title: 'Marca / Modelo', key: 'disp', render: (_, r) => <div><div style={{ fontWeight: 600 }}>{r.marca}</div><div style={{ fontSize: 12, color: hssColors.textSecondary }}>{r.modelo}</div></div> },
    { title: 'Canales', dataIndex: 'num_canales', key: 'can', render: v => v ? <Tag color="blue">{v} CH</Tag> : '—' },
    { title: 'Cámaras', dataIndex: 'total_camaras', key: 'cams', render: v => <Badge count={v} showZero style={{ background: v > 0 ? hssColors.success : hssColors.gray400 }} /> },
    estadoCol, vehiculoCol, accionesCol('dvr'),
  ];

  const columnsCamaras = [
    { title: 'N° Serie', dataIndex: 'numero_serie', key: 'serie', render: v => <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{v}</span> },
    { title: 'Marca / Modelo', key: 'disp', render: (_, r) => <div><div style={{ fontWeight: 600 }}>{r.marca}</div><div style={{ fontSize: 12, color: hssColors.textSecondary }}>{r.modelo}</div></div> },
    { title: 'Tipo', dataIndex: 'tipo', key: 'tipo', render: t => t ? <Tag color="purple">{t}</Tag> : '—' },
    { title: 'DVR', dataIndex: 'dvr', key: 'dvr', render: v => v ? <span style={{ fontSize: 12 }}>{v}</span> : <span style={{ color: hssColors.gray400 }}>Sin DVR</span> },
    estadoCol, vehiculoCol, accionesCol('camara'),
  ];

  const columnsRouters = [
    { title: 'N° Serie', dataIndex: 'numero_serie', key: 'serie', render: v => <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{v}</span> },
    { title: 'Marca / Modelo', key: 'disp', render: (_, r) => <div><div style={{ fontWeight: 600 }}>{r.marca}</div><div style={{ fontSize: 12, color: hssColors.textSecondary }}>{r.modelo}</div></div> },
    { title: 'Línea SIM', dataIndex: 'numero_linea', key: 'linea', render: v => v ? <span style={{ fontFamily: 'monospace' }}>{v}</span> : '—' },
    estadoCol, vehiculoCol, accionesCol('router'),
  ];

  const { tipo: mTipo, modo: mModo, item: mItem } = modal;
  const modalAbierto = !!mTipo;

  const titulos = {
    dvr: { nuevo: 'Nuevo DVR', editar: `Editar DVR — ${mItem?.numero_serie}`, asignar: `Asignar vehículo — DVR ${mItem?.numero_serie}` },
    camara: { nuevo: 'Nueva Cámara', editar: `Editar cámara — ${mItem?.numero_serie}`, asignar: `Asignar vehículo — Cámara ${mItem?.numero_serie}` },
    router: { nuevo: 'Nuevo Router', editar: `Editar router — ${mItem?.numero_serie}`, asignar: `Asignar vehículo — Router ${mItem?.numero_serie}` },
  };

  const tabItems = [
    {
      key: 'dvr', label: <Space><DatabaseOutlined />DVR<Badge count={dvrs.length} style={{ background: hssColors.primary }} /></Space>,
      children: <Table columns={columnsDVR} dataSource={filtrar(dvrs)} rowKey="id" loading={loading} scroll={{ x: 'max-content' }} pagination={{ pageSize: 15 }} />,
    },
    {
      key: 'camara', label: <Space><VideoCameraOutlined />Cámaras<Badge count={camaras.length} style={{ background: hssColors.success }} /></Space>,
      children: <Table columns={columnsCamaras} dataSource={filtrar(camaras)} rowKey="id" loading={loading} scroll={{ x: 'max-content' }} pagination={{ pageSize: 15 }} />,
    },
    {
      key: 'router', label: <Space><WifiOutlined />Routers<Badge count={routers.length} style={{ background: '#16a085' }} /></Space>,
      children: <Table columns={columnsRouters} dataSource={filtrar(routers)} rowKey="id" loading={loading} scroll={{ x: 'max-content' }} pagination={{ pageSize: 15 }} />,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <SafetyCertificateOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>HSS Vision</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>
                Gestión de equipos CCTV — DVR · Cámaras · Routers
              </p>
            </div>
          </div>
          <Space>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => abrirModal(tab, 'nuevo')}
              size="large"
              style={{ height: 44, borderRadius: 8, fontWeight: 600 }}
            >
              Nuevo {tab === 'dvr' ? 'DVR' : tab === 'camara' ? 'Cámara' : 'Router'}
            </Button>
          </Space>
        </div>
      </div>

      <div style={{ padding: '24px 32px', flex: 1 }}>
        {/* Stats */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'DVRs', value: dvrs.length, icon: <DatabaseOutlined />, color: hssColors.primary },
            { label: 'Cámaras', value: camaras.length, icon: <VideoCameraOutlined />, color: hssColors.success },
            { label: 'Routers', value: routers.length, icon: <WifiOutlined />, color: '#e67e22' },
            { label: 'Instalados', value: [...dvrs, ...camaras, ...routers].filter(e => e.estado === 'instalado').length, icon: <SafetyCertificateOutlined />, color: hssColors.success },
          ].map(s => (
            <Card key={s.label} loading={loading} style={{ flex: 1, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 28, color: s.color }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{s.label}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
          <div style={{ marginBottom: 16 }}>
            <Input placeholder="Buscar por N° serie, marca, modelo, vehículo..."
              prefix={<SearchOutlined style={{ color: hssColors.gray400 }} />}
              style={{ width: 420, borderRadius: 8 }} size="large"
              value={search} onChange={e => setSearch(e.target.value)} allowClear />
          </div>
          <Tabs activeKey={tab} onChange={setTab} items={tabItems} />
        </Card>
      </div>

      {/* Modal unificado */}
      <Modal
        title={
          <Space style={{ fontSize: 18, fontWeight: 700, color: hssColors.primary }}>
            {mTipo === 'dvr' ? <DatabaseOutlined /> : mTipo === 'camara' ? <VideoCameraOutlined /> : <WifiOutlined />}
            <span>{mTipo && mModo && titulos[mTipo]?.[mModo]}</span>
          </Space>
        }
        open={modalAbierto}
        onCancel={cerrarModal}
        footer={null}
        width={520}
      >
        <Form form={form} onFinish={onSubmit} layout="vertical" style={{ marginTop: 20 }}>

          {/* Campos asignar */}
          {mModo === 'asignar' && (
            <Form.Item name="vehiculo_chapa" label="Vehículo (dejar vacío para desasignar)">
              <Select size="large" showSearch allowClear placeholder="Seleccionar vehículo..." optionFilterProp="label">
                {vehiculos.map(v => <Option key={v.chapa} value={v.chapa} label={`${v.chapa} — ${v.marca} ${v.modelo}`}>{v.chapa} — {v.marca} {v.modelo}</Option>)}
              </Select>
            </Form.Item>
          )}

          {/* Campos comunes nuevo/editar */}
          {mModo !== 'asignar' && (
            <>
              {mModo === 'nuevo' && (
                <Form.Item name="numero_serie" label="Número de serie" rules={[{ required: true, message: 'Requerido' }]}>
                  <Input size="large" style={{ borderRadius: 8, fontFamily: 'monospace' }} placeholder="S/N del equipo" />
                </Form.Item>
              )}
              <Form.Item name="marca" label="Marca">
                {mTipo === 'dvr' && <CreatableSelect options={marcasDvr} onOptionsChange={setMarcasDvr} placeholder="Seleccionar o agregar marca..." />}
                {mTipo === 'camara' && <CreatableSelect options={marcasCam} onOptionsChange={setMarcasCam} placeholder="Seleccionar o agregar marca..." />}
                {mTipo === 'router' && <CreatableSelect options={marcasRouter} onOptionsChange={setMarcasRouter} placeholder="Seleccionar o agregar marca..." />}
              </Form.Item>
              <Form.Item name="modelo" label="Modelo">
                {mTipo === 'dvr' && <CreatableSelect options={modelosDvr} onOptionsChange={setModelosDvr} placeholder="Seleccionar o agregar modelo..." />}
                {mTipo === 'camara' && <CreatableSelect options={modelosCam} onOptionsChange={setModelosCam} placeholder="Seleccionar o agregar modelo..." />}
                {mTipo === 'router' && <CreatableSelect options={modelosRouter} onOptionsChange={setModelosRouter} placeholder="Seleccionar o agregar modelo..." />}
              </Form.Item>

              {/* DVR: canales + credenciales */}
              {mTipo === 'dvr' && (
                <>
                  <Form.Item name="num_canales" label="Número de canales">
                    <InputNumber size="large" style={{ width: '100%', borderRadius: 8 }} min={1} max={64} placeholder="Ej: 4, 8, 16..." />
                  </Form.Item>
                  <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12, color: hssColors.textSecondary, margin: '8px 0' }}>Credenciales</Divider>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <Form.Item name="usuario_web" label="Usuario plataforma web">
                      <Input size="large" style={{ borderRadius: 8 }} placeholder="admin" />
                    </Form.Item>
                    <Form.Item name="password_web" label="Contraseña plataforma web">
                      <Input.Password size="large" style={{ borderRadius: 8 }} placeholder="Consultores2016" />
                    </Form.Item>
                    <Form.Item name="usuario_local" label="Usuario local (DVR)">
                      <Input size="large" style={{ borderRadius: 8 }} placeholder="admin" />
                    </Form.Item>
                    <Form.Item name="password_local" label="Contraseña local (DVR)">
                      <Input.Password size="large" style={{ borderRadius: 8 }} placeholder="Consultores2016" />
                    </Form.Item>
                  </div>
                  <Form.Item name="verification_code" label="Verification Code (código de fábrica)">
                    <Input size="large" style={{ borderRadius: 8, fontFamily: 'monospace' }} placeholder="Ej: ABCD1234" />
                  </Form.Item>
                </>
              )}

              {/* Cámara: tipo + DVR */}
              {mTipo === 'camara' && (
                <>
                  <Form.Item name="tipo" label="Tipo de cámara">
                    <Select size="large" allowClear placeholder="Seleccionar tipo">
                      {TIPOS_CAMARA.map(t => <Option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</Option>)}
                    </Select>
                  </Form.Item>
                  <Form.Item name="dvr_id" label="DVR asociado (opcional)">
                    <Select size="large" allowClear showSearch placeholder="Buscar DVR..." optionFilterProp="label">
                      {dvrs.map(d => <Option key={d.id} value={d.id} label={`${d.numero_serie} — ${d.marca} ${d.modelo}`}>{d.numero_serie} — {d.marca} {d.modelo}</Option>)}
                    </Select>
                  </Form.Item>
                </>
              )}

              {/* Router: línea SIM + credenciales */}
              {mTipo === 'router' && (
                <>
                  <Form.Item name="numero_linea" label="Número de línea (SIM)">
                    <Input size="large" style={{ borderRadius: 8, fontFamily: 'monospace' }} placeholder="Ej: 0981123456" />
                  </Form.Item>
                  <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12, color: hssColors.textSecondary, margin: '8px 0' }}>Credenciales</Divider>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <Form.Item name="usuario_web" label="Usuario plataforma web">
                      <Input size="large" style={{ borderRadius: 8 }} placeholder="admin" />
                    </Form.Item>
                    <Form.Item name="password_web" label="Contraseña plataforma web">
                      <Input.Password size="large" style={{ borderRadius: 8 }} placeholder="Consultores2016" />
                    </Form.Item>
                    <Form.Item name="wifi_nombre" label="Nombre WiFi (SSID)">
                      <Input size="large" style={{ borderRadius: 8 }} placeholder="HSS Vision" />
                    </Form.Item>
                    <Form.Item name="wifi_password" label="Contraseña WiFi">
                      <Input.Password size="large" style={{ borderRadius: 8 }} placeholder="Ver etiqueta del router" />
                    </Form.Item>
                  </div>
                </>
              )}

              <Form.Item name="estado" label="Estado">
                <Select size="large">
                  <Option value="instalado">Instalado</Option>
                  <Option value="desinstalado">Desinstalado</Option>
                  <Option value="baja">Baja</Option>
                </Select>
              </Form.Item>

              {mModo === 'nuevo' && (
                <Form.Item name="vehiculo_chapa" label="Asignar a vehículo (opcional)">
                  <Select size="large" showSearch allowClear placeholder="Buscar chapa..." optionFilterProp="label">
                    {vehiculos.map(v => <Option key={v.chapa} value={v.chapa} label={`${v.chapa} — ${v.marca} ${v.modelo}`}>{v.chapa} — {v.marca} {v.modelo}</Option>)}
                  </Select>
                </Form.Item>
              )}
            </>
          )}

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={cerrarModal} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>
                {mModo === 'asignar' ? 'Confirmar asignación' : mModo === 'editar' ? 'Guardar cambios' : 'Guardar'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Credenciales */}
      <Modal
        title={
          <Space style={{ color: '#d4a017', fontWeight: 700, fontSize: 16 }}>
            <KeyOutlined />
            <span>
              Credenciales — {credModal.tipo === 'dvr' ? 'DVR' : 'Router'} {credModal.item?.numero_serie}
            </span>
          </Space>
        }
        open={credModal.open}
        onCancel={() => setCredModal({ open: false, tipo: null, item: null })}
        footer={null}
        width={480}
      >
        {credModal.item && (
          <div style={{ marginTop: 8 }}>
            {credModal.tipo === 'dvr' ? (
              <>
                <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12, color: hssColors.textSecondary }}>Plataforma web</Divider>
                <CredRow label="Usuario" valor={credModal.item.usuario_web} onCopy={() => copiar(credModal.item.usuario_web, 'Usuario web')} />
                <CredRow label="Contraseña" valor={credModal.item.password_web} onCopy={() => copiar(credModal.item.password_web, 'Contraseña web')} />
                <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12, color: hssColors.textSecondary }}>Local (DVR físico)</Divider>
                <CredRow label="Usuario" valor={credModal.item.usuario_local} onCopy={() => copiar(credModal.item.usuario_local, 'Usuario local')} />
                <CredRow label="Contraseña" valor={credModal.item.password_local} onCopy={() => copiar(credModal.item.password_local, 'Contraseña local')} />
                <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12, color: hssColors.textSecondary }}>Verificación</Divider>
                <CredRow label="Verification Code" valor={credModal.item.verification_code} onCopy={() => copiar(credModal.item.verification_code, 'Verification Code')} mono />
              </>
            ) : (
              <>
                <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12, color: hssColors.textSecondary }}>Plataforma web</Divider>
                <CredRow label="Usuario" valor={credModal.item.usuario_web} onCopy={() => copiar(credModal.item.usuario_web, 'Usuario web')} />
                <CredRow label="Contraseña" valor={credModal.item.password_web} onCopy={() => copiar(credModal.item.password_web, 'Contraseña web')} />
                <Divider orientation="left" orientationMargin={0} style={{ fontSize: 12, color: hssColors.textSecondary }}>WiFi</Divider>
                <CredRow label="Nombre (SSID)" valor={credModal.item.wifi_nombre} onCopy={() => copiar(credModal.item.wifi_nombre, 'SSID')} />
                <CredRow label="Contraseña" valor={credModal.item.wifi_password} onCopy={() => copiar(credModal.item.wifi_password, 'Contraseña WiFi')} />
              </>
            )}
          </div>
        )}
      </Modal>

      <Footer />
    </div>
  );
};

const CredRow = ({ label, valor, onCopy, mono }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
    <span style={{ fontSize: 13, color: '#666', minWidth: 140 }}>{label}</span>
    <Space>
      <span style={{
        fontFamily: mono ? 'monospace' : 'inherit',
        fontWeight: 600, fontSize: 14,
        color: valor ? '#1a1a1a' : '#bbb',
      }}>
        {valor || '—'}
      </span>
      {valor && (
        <Button type="text" size="small" icon={<CopyOutlined />} onClick={onCopy}
          style={{ color: '#888' }} />
      )}
    </Space>
  </div>
);

export default HSSVision;
